import React, { useMemo } from 'react';

import { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { useAgentPulseAudienceSelectionQuery } from '~/types/api.graphql';

import A2uiSurface, { SurfaceDataModel } from './A2uiSurface';
import {
  findPulseFormBindings,
  PeopleCountLine,
  readPulseFormSelection,
  sanitizePulseFilters,
  usePulseSurfacePeopleCount,
} from './PeopleCount';

type Props = Omit<React.ComponentProps<typeof A2uiSurface>, 'onDataModelChange'> & {
  /** The Pulse audience the agent's message links to: the one an edit form without geo filters edits. */
  linkedAudienceId?: string | null;
};

/**
 * An agent-composed surface that, when it is a Pulse audience form (a topics picker, optionally geo
 * filters), shows a live count of the people the user's selection matches under the form. The count
 * line is the frontend's, not an agent component, and editing the form sends nothing to the agent.
 * Any other surface renders exactly like A2uiSurface.
 */
const A2uiSurfaceWithPeopleCount = ({ linkedAudienceId, ...props }: Props): JSX.Element => {
  const bindings = useMemo(() => findPulseFormBindings(props.operations), [props.operations]);
  // An edit form without a geo filter field keeps the audience's current geo filters, so count those.
  const editedAudienceId = bindings && !bindings.geoFilterPaths.length ? linkedAudienceId ?? null : null;
  const { data, error } = useAgentPulseAudienceSelectionQuery({
    variables: { audienceId: editedAudienceId ?? '' },
    skip: !editedAudienceId,
    fetchPolicy: props.disabled ? 'cache-only' : 'cache-first',
  });

  const baseline = useMemo(() => {
    if (!bindings || !editedAudienceId || error) return undefined;
    if (!data) return null;
    return {
      topicIds: readPulseFormSelection(bindings, readInitialDataModel(props.operations)).topicIds,
      filters: sanitizePulseFilters(data.audience?.filters),
    };
  }, [bindings, editedAudienceId, error, data, props.operations]);

  // Submitted and stale forms don't query; they keep a count only if it is already cached.
  const { peopleCount, onDataModelChange } = usePulseSurfacePeopleCount({
    bindings,
    baseline,
    active: !props.disabled,
  });

  return (
    <>
      <A2uiSurface {...props} onDataModelChange={onDataModelChange} />
      {peopleCount && <PeopleCountLine peopleCount={peopleCount} />}
    </>
  );
};

// The data model the agent prefilled the form with, before the user edits anything.
const readInitialDataModel = (operations: A2uiOperation[]): SurfaceDataModel => {
  const update = operations
    .map((operation) => operation.updateDataModel as { path?: unknown; value?: unknown } | undefined)
    .find((dataModelUpdate) => dataModelUpdate?.path === '/');
  const value = update?.value;
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as SurfaceDataModel) : {};
};

export default A2uiSurfaceWithPeopleCount;
